import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields, AGGREGATION_TYPES } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function HeatmapConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray.filter((f) => f.aggregatable),
        allFields: fieldsArray,
      }
    }

    // Para otras fuentes de datos, usar lógica existente
    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const heatmapConfig = config.heatmapConfig || { xAxisKey: '', yAxisKey: '', valueKey: '', aggregation: 'SUM' as const }
  const isValid = Boolean(
    config.metadata?.title &&
    heatmapConfig.xAxisKey &&
    heatmapConfig.yAxisKey &&
    heatmapConfig.valueKey &&
    heatmapConfig.aggregation
  )

  const COLOR_SCHEMES = [
    { value: 'green-yellow-red', label: 'Verde → Amarillo → Rojo', gradient: 'bg-gradient-to-r from-green-500 via-yellow-500 to-red-500' },
    { value: 'blue-white-red', label: 'Azul → Blanco → Rojo', gradient: 'bg-gradient-to-r from-blue-500 via-white to-red-500' },
    { value: 'purple-white-green', label: 'Púrpura → Blanco → Verde', gradient: 'bg-gradient-to-r from-purple-500 via-white to-green-500' },
  ]

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Mapa de Calor</h2>
      <p className="text-gray-600 mb-6">
        Visualiza intensidades en una matriz de dos dimensiones
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Intensidad Provincia × Especie"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Eje X (Dimensión horizontal) */}
        <div>
          <label className="block font-medium mb-2">
            Dimensión Horizontal (Eje X) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={heatmapConfig.xAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                heatmapConfig: { ...heatmapConfig, xAxisKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Categoría para las columnas (ej: provincia, año)
          </p>
        </div>

        {/* Eje Y (Dimensión vertical) */}
        <div>
          <label className="block font-medium mb-2">
            Dimensión Vertical (Eje Y) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={heatmapConfig.yAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                heatmapConfig: { ...heatmapConfig, yAxisKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Categoría para las filas (ej: especie, formación)
          </p>
        </div>

        {/* Campo de valor */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Valor (Intensidad) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={heatmapConfig.valueKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                heatmapConfig: { ...heatmapConfig, valueKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Valor numérico que determinará la intensidad del color
          </p>
        </div>

        {/* Tipo de agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación de Agregación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-4 text-center transition-all
                  ${
                    (heatmapConfig.aggregation || 'SUM') === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    heatmapConfig: { ...heatmapConfig, aggregation: agg.value as any },
                  })
                }
              >
                <div className="text-2xl mb-1">{agg.icon}</div>
                <div className="font-medium text-sm">{agg.value}</div>
                <div className="text-xs text-gray-500 mt-1">{agg.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Esquema de color */}
        <div>
          <label className="block font-medium mb-2">Esquema de Color</label>
          <div className="space-y-3">
            {COLOR_SCHEMES.map((scheme) => (
              <button
                key={scheme.value}
                type="button"
                className={`
                  w-full border-2 rounded p-3 text-left transition-all
                  ${
                    (heatmapConfig.colorScheme || 'green-yellow-red') === scheme.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    heatmapConfig: { ...heatmapConfig, colorScheme: scheme.value as any },
                  })
                }
              >
                <div className="flex items-center gap-3">
                  <div className={`w-32 h-8 rounded ${scheme.gradient}`} />
                  <span className="font-medium">{scheme.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Mostrar valores en celdas */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="showValues"
            checked={heatmapConfig.showValues !== false}
            onChange={(e) =>
              onChange({
                ...config,
                heatmapConfig: { ...heatmapConfig, showValues: e.target.checked },
              })
            }
            className="w-5 h-5"
          />
          <label htmlFor="showValues" className="font-medium cursor-pointer">
            Mostrar valores numéricos en las celdas
          </label>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}